import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Briefcase, Sparkles, Activity, ExternalLink, Plus, ChevronRight, Compass } from "lucide-react";
import { Link } from "react-router-dom";

const statusColor = {
  live: "text-bm-green border-bm-green/50",
  launching: "text-bm-amber border-bm-amber/50",
  concept: "text-bm-cyan border-bm-cyan/50",
  paused: "text-bm-danger border-bm-danger/50",
  wip: "text-bm-cyan border-bm-cyan/50",
};

function MetricTile({ label, value }) {
  return (
    <div className="bg-bm-elevated border border-white/10 p-3">
      <div className="label-mono text-bm-dim">{label}</div>
      <div className="font-heading text-2xl font-black mt-1 text-white">{value}</div>
    </div>
  );
}

function ProductLineCard({ line }) {
  return (
    <div className="bm-card p-4 flex flex-col gap-2" data-testid={`line-${line.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-mono text-bm-cyan">{line.format}</span>
          {line.flagship && <span className="label-mono text-bm-amber border border-bm-amber/40 px-1 py-0.5">FLAGSHIP</span>}
        </div>
        <span className={`label-mono px-1.5 py-0.5 border ${statusColor[line.status] || ""}`}>
          {line.status}
        </span>
      </div>
      <h3 className="font-heading text-lg font-bold text-white">{line.name}</h3>
      <p className="text-sm text-bm-muted leading-relaxed">{line.description}</p>
      <div className="border-t border-white/5 pt-2 mt-1 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-xs">
        <div className="text-bm-dim">POSITIONING</div>
        <div className="text-bm-text/90">{line.positioning}</div>
        {line.target_audience && (<>
          <div className="text-bm-dim">TARGET</div>
          <div className="text-bm-text/90">{line.target_audience}</div>
        </>)}
        {line.price_inr_range && (<>
          <div className="text-bm-dim">PRICE</div>
          <div className="text-bm-amber">{line.price_inr_range}</div>
        </>)}
      </div>
    </div>
  );
}

function ProjectCard({ project }) {
  const liveLines = project.product_lines.filter((l) => l.status === "live").length;
  const launchLines = project.product_lines.filter((l) => l.status === "launching").length;
  return (
    <div className="bm-card p-5 fade-up" data-testid={`project-${project.slug}`}>
      <div className="flex items-start gap-4 mb-4">
        <div
          className="w-12 h-12 shrink-0 flex items-center justify-center font-heading font-black text-2xl"
          style={{ background: project.color_accent, color: "#000" }}
          data-testid={`project-emblem-${project.slug}`}
        >
          {project.name[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`label-mono px-1.5 py-0.5 border ${statusColor[project.status] || ""}`}>
              {project.status}
            </span>
            <span className="label-mono text-bm-dim">FOUNDED {project.founded}</span>
            <span className="label-mono text-bm-dim">· {project.category_focus}</span>
          </div>
          <h2 className="font-heading text-3xl font-black tracking-tight leading-tight text-white">
            {project.name}
          </h2>
          <p className="text-sm text-bm-muted mt-1">{project.tagline}</p>
        </div>
      </div>

      <p className="text-sm text-bm-text/85 leading-relaxed mb-4">{project.description}</p>

      <div className="border-t border-white/5 pt-3 mb-4">
        <div className="label-mono text-bm-dim mb-1">POSITIONING</div>
        <div className="text-sm text-white font-mono leading-relaxed">{project.positioning}</div>
      </div>

      {/* Metrics */}
      {project.metrics && Object.keys(project.metrics).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid={`project-metrics-${project.slug}`}>
          {Object.entries(project.metrics).map(([k, v]) => (
            <MetricTile key={k} label={k.replaceAll("_", " ").toUpperCase()} value={v} />
          ))}
        </div>
      )}

      {/* Tags */}
      {project.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`project-tags-${project.slug}`}>
          {project.tags.map((t) => (
            <span key={t} className="text-[11px] font-mono px-1.5 py-0.5 border border-bm-cyan/40 text-bm-cyan">
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Notes */}
      {project.notes && (
        <div className="border-t border-white/5 pt-3 mb-4" data-testid={`project-notes-${project.slug}`}>
          <div className="label-mono text-bm-dim mb-1.5">NOTES</div>
          <div className="text-sm text-bm-text/90 leading-relaxed italic">{project.notes}</div>
        </div>
      )}

      {/* Reference brands (mood / positioning) */}
      {project.reference_brands_detailed?.length > 0 && (
        <div className="border-t border-white/5 pt-3 mb-4" data-testid={`project-refs-${project.slug}`}>
          <div className="label-mono text-bm-dim mb-1.5">REFERENCE BRANDS · MOOD / POSITIONING</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {project.reference_brands_detailed.map((ref) => (
              <div key={ref.name} className="bg-bm-elevated border border-white/10 p-3" data-testid={`ref-brand-${ref.name}`}>
                <div className="font-heading font-bold text-white">{ref.name}</div>
                {ref.market && <div className="label-mono text-bm-amber mt-0.5">{ref.market}</div>}
                {ref.description && <div className="text-xs text-bm-muted mt-1.5 leading-relaxed">{ref.description}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product lines */}
      <div className="space-y-1 mb-3">
        <div className="flex items-center justify-between">
          <div className="label-mono text-bm-cyan flex items-center gap-1.5">
            <Briefcase className="w-3 h-3" />PRODUCT LINES · {project.product_lines.length}
          </div>
          <div className="label-mono text-bm-dim">
            {liveLines} live · {launchLines} launching
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {project.product_lines.map((line) => <ProductLineCard key={line.id} line={line} />)}
      </div>

      <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-3">
        <Link
          to={`/concepts?project=${project.slug}`}
          data-testid={`generate-concept-${project.slug}`}
          className="inline-flex items-center gap-2 px-3 h-9 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />GENERATE CONCEPT FOR {project.name.toUpperCase()}
          <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);

  useEffect(() => { api.projects().then((d) => setProjects(d.projects)); }, []);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber">MEOLAA PORTFOLIO</div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            Our houses.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-2xl">
            Internal brand portfolio — every signal, insight, and opportunity routes back to one of these houses. New concepts generated here become tomorrow's product lines.
          </p>
        </div>
        <Link
          to="/projects/needscope"
          data-testid="open-needscope-btn"
          className="flex items-center gap-2 px-3 h-10 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors"
        >
          <Compass className="w-3.5 h-3.5" />
          OPEN NEEDSCOPE GRID
        </Link>
      </div>

      <div className="space-y-5" data-testid="project-list">
        {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
      </div>
    </div>
  );
}
